<template>
  <div class="darknet-importer">
    <div class="darknet-importer__group">
      <div class="darknet-importer__heading">
        <h4>{{ sourceLabel }}</h4>
      </div>
      <div class="file-upload-container">
        <input
          id="darknet-file-select"
          class="d-none"
          type="file"
          multiple
          :accept="uploadAccept || undefined"
          @change="handleSelectFileInput"
        >
        <div
          class="file-upload-btn mt-3"
          :class="{ _highlight: dropHighlighted }"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent=""
          @dragleave="handleDragLeave"
          @drop="dropHighlighted = false"
          @drop.stop.prevent="handleFileDrop"
        >
          <SVGIcon
            v-if="!parsingFiles"
            id="upload-icon"
            class="file-upload-btn__upload-icon"
            :iconName="'cloud_upload'"
            :width="'100%'"
            :height="'200px'"
          />
          <div v-else class="file-upload-btn__parsing-files">
            <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
          </div>
          <div id="file-upload-btn-text" class="file-upload-btn__text">
            <span>
              {{ uploadHintPrefix }}
              <span
                v-if="allowsFileSelection"
                class="highlight"
                @click="!parsingFiles ? chooseFiles() : null"
              >select files</span>
              <span v-if="allowsFileSelection && allowsFolderSelection">, or </span>
              <span
                v-if="allowsFolderSelection"
                class="highlight"
                @click="!parsingFiles ? chooseSourceFolder() : null"
              >select parent folder</span>
            </span>
          </div>
          <template v-if="fileObjs && fileObjs.length <= 20">
            <div v-if="fileObjs && fileObjs.length > 0" id="file-upload-grid" class="file-grid scrollbar">
              <div v-for="(file, i) in fileObjs" :key="`file-${i}`" class="file-grid__cell">
                <span>{{ file.name }}</span>
                <IconButton
                  :id="`file-${i}-remove-icon`"
                  class="file-grid__remove-icon"
                  :icon="'cancel_filled'"
                  :width="16"
                  :height="16"
                  @click="removeFile(i)"
                />
              </div>
            </div>
          </template>
          <div v-else-if="fileObjs && fileObjs.length > 20" class="file-upload-btn__text">
            <span>{{ fileObjs.length }} files selected</span>
          </div>
        </div>
      </div>

      <v-checkbox
        v-model="shouldCreateImageGroups"
        density="compact"
        hide-details="true"
        :label="imageGroupsLabel"
      />
    </div>

    <div class="darknet-importer__group">
      <div class="darknet-importer__heading">
        <h4>{{ destinationLabel }}</h4>
      </div>
      <SchemaGroup
        ref="schemaGroupRef"
        v-model:params="destinationParams"
        :fields="destinationFields"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { showDirectoryPicker } from 'file-system-access';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { encodeID } from '@/assets/js/utils.js';
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import { useImporterStore } from '@/stores/useImporterStore.js';
import { appSchema } from './schema.js';

defineProps({
  appDetails: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['completed']);

const $toast = useToast();
const router = useRouter();
const store = useStore();
const importerStore = useImporterStore();
const dataConnect = new DatastoreConnect();

const schemaGroupRef = ref(null);
const parsingFiles = ref(false);
const dropHighlighted = ref(false);
const dragCounter = ref(0);
const fileObjs = ref([]);
const shouldCreateImageGroups = ref(Boolean(appSchema.find((field) => field.name === 'source')?.children?.find((field) => field.type === 'bool' && field.name === 'image_groups')?.default));
const imageGroupsToCreate = ref([]);
const destinationParams = ref({});
const createdDataset = ref(null);

const sourceGroup = computed(() => appSchema.find((field) => field.name === 'source') || {});
const destinationGroup = computed(() => appSchema.find((field) => field.name === 'destination') || {});
const sourceLabel = computed(() => sourceGroup.value?.label || 'Source');
const destinationLabel = computed(() => destinationGroup.value?.label || 'Destination');
const sourceFields = computed(() => sourceGroup.value?.children || []);
const destinationFields = computed(() => destinationGroup.value?.children || []);
const sourceUploadField = computed(() => sourceFields.value.find((field) => field.type === 'upload') || {});
const imageGroupsField = computed(() => sourceFields.value.find((field) => field.type === 'bool' && field.name === 'image_groups') || {});

const uploadAccept = computed(() => {
  const filter = sourceUploadField.value?.files_filter;
  return Array.isArray(filter) && filter.length > 0 ? filter.join(',') : null;
});

const allowedExtensions = computed(() => {
  const filter = sourceUploadField.value?.files_filter;
  if (!Array.isArray(filter) || filter.length === 0) {
    return [];
  }

  return filter
    .map((extension) => String(extension).trim().replace(/^\./, '').toLowerCase())
    .filter((extension) => extension.length > 0);
});

const selectionMode = computed(() => {
  const mode = String(sourceUploadField.value?.selection_mode || 'mixed').toLowerCase();
  return ['mixed', 'files', 'folder'].includes(mode) ? mode : 'mixed';
});

const allowsFileSelection = computed(() => selectionMode.value === 'mixed' || selectionMode.value === 'files');
const allowsFolderSelection = computed(() => selectionMode.value === 'mixed' || selectionMode.value === 'folder');
const uploadHintPrefix = computed(() => (selectionMode.value === 'mixed' ? 'Drag and drop, ' : 'Drag and drop or '));
const imageGroupsLabel = computed(() => imageGroupsField.value?.label || 'Create image groups from folder names');

function chooseFiles() {
  if (!allowsFileSelection.value) {
    return;
  }
  const input = document.getElementById('darknet-file-select');
  input?.click();
}

function handleSelectFileInput(event) {
  fileObjs.value = Array.from(event.target.files || []).filter((file) => isAllowedFile(file.name));
}

function removeFile(index) {
  fileObjs.value.splice(index, 1);
}

function handleDragEnter() {
  dragCounter.value += 1;
  dropHighlighted.value = true;
}

function handleDragLeave() {
  dragCounter.value -= 1;
  if (dragCounter.value <= 0) {
    dropHighlighted.value = false;
    dragCounter.value = 0;
  }
}

async function handleDirectory(dirhandle, relativePath) {
  const promises = [];

  for await (const [name, handle] of dirhandle) {
    if (handle.kind === 'directory') {
      if (dirhandle.name === relativePath) {
        imageGroupsToCreate.value.push(name);
      }
      const newRelativePath = relativePath ? `${relativePath}/${name}` : name;
      promises.push(handleDirectory(handle, newRelativePath));
    } else {
      promises.push(
        handle.getFile().then((file) => {
          file.relativePath = `${relativePath}/${name}`;
          fileObjs.value.push(file);
        }),
      );
    }
  }

  await Promise.all(promises);
}

function isAllowedFile(fileName) {
  if (allowedExtensions.value.length === 0) {
    return true;
  }
  const extension = String(fileName || '').split('.').pop()?.toLowerCase();
  return allowedExtensions.value.includes(extension);
}

async function chooseSourceFolder() {
  if (!allowsFolderSelection.value) {
    return;
  }
  try {
    const directoryHandle = await showDirectoryPicker({ _preferPolyfill: false });
    parsingFiles.value = true;
    fileObjs.value = [];
    imageGroupsToCreate.value = [];
    await handleDirectory(directoryHandle, directoryHandle.name);
  } catch (error) {
    if (!String(error).includes('AbortError')) {
      $toast.open({
        queue: true,
        duration: 0,
        message: String(error),
        type: 'error',
        dismissible: true,
        position: 'top',
      });
    }
  } finally {
    parsingFiles.value = false;
  }
}

async function collectDroppedDirectoryFiles(directoryHandle, relativePath = directoryHandle.name) {
  const files = [];

  for await (const [name, handle] of directoryHandle) {
    if (handle.kind === 'directory') {
      files.push(...await collectDroppedDirectoryFiles(handle, `${relativePath}/${name}`));
    } else {
      const file = await handle.getFile();
      if (isAllowedFile(file.name)) {
        file.relativePath = `${relativePath}/${name}`;
        files.push(file);
      }
    }
  }

  return files;
}

async function listTopLevelDirectories(directoryHandle) {
  const directoryNames = [];

  for await (const [name, handle] of directoryHandle) {
    if (handle.kind === 'directory') {
      directoryNames.push(name);
    }
  }

  return directoryNames;
}

async function collectDroppedEntry(entry) {
  if (!entry) {
    return { files: [], imageGroups: [] };
  }

  if (entry.kind === 'directory') {
    if (!allowsFolderSelection.value) {
      return { files: [], imageGroups: [] };
    }

    const [imageGroups, files] = await Promise.all([
      listTopLevelDirectories(entry),
      collectDroppedDirectoryFiles(entry),
    ]);

    return { files, imageGroups };
  }

  if (!allowsFileSelection.value) {
    return { files: [], imageGroups: [] };
  }

  const file = await entry.getFile();
  return {
    files: isAllowedFile(file.name) ? [file] : [],
    imageGroups: [],
  };
}

async function handleFileDrop(event) {
  dragCounter.value = 0;
  dropHighlighted.value = false;

  const items = Array.from(event.dataTransfer?.items || []);
  if (items.length > 0 && typeof items[0].getAsFileSystemHandle === 'function') {
    const entries = await Promise.all(
      items
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFileSystemHandle()),
    );

    const droppedEntries = await Promise.all(entries.map((entry) => collectDroppedEntry(entry)));
    imageGroupsToCreate.value.push(...droppedEntries.flatMap((entry) => entry.imageGroups));
    fileObjs.value = droppedEntries.flatMap((entry) => entry.files);
    return;
  }

  if (!allowsFileSelection.value) {
    fileObjs.value = [];
    return;
  }

  fileObjs.value = Array.from(event.dataTransfer?.files || []).filter((file) => isAllowedFile(file.name));
}

function buildImportParams() {
  const datasetInfo = getExistingDatasetInfo();
  const newDataset = getNewDatasetInfo();
  const datasetId = Number(datasetInfo?.dataset_id || createdDataset.value?.id || 0);
  const dataset = store.state?.datasets?.datasetList?.find((entry) => entry.id === datasetId) || createdDataset.value;

  return {
    import_type: 'darknet',
    dataset_id: datasetId,
    dataset_name: dataset?.name || '',
    project_id: Number(datasetInfo?.project_id || createdDataset.value?.project_id || newDataset?.project_id || store.state.projects.currentProject?.id || 0),
    annotation_set_id: datasetInfo?.annotation_set_id || createdDataset.value?.annotation_sets?.[0]?.id || null,
    tag_name: datasetInfo?.tag_name || '',
    train_group_name: datasetInfo?.train_group_name || '',
    val_group_name: datasetInfo?.val_group_name || '',
    create_image_groups: shouldCreateImageGroups.value ? imageGroupsToCreate.value : undefined,
  };
}

function isCreatingNewDataset() {
  return Object.prototype.hasOwnProperty.call(destinationParams.value || {}, 'new_dataset');
}

function getExistingDatasetInfo() {
  return destinationParams.value?.new_dataset__on_false?.dataset_info || null;
}

function getNewDatasetInfo() {
  return destinationParams.value?.new_dataset || {};
}

function getDatasetRoute(projectId, datasetId) {
  return router.resolve({
    name: 'datasets.manager',
    params: {
      projectId: encodeID(projectId, 'p'),
    },
    hash: `#dataset-${datasetId}`,
  });
}

function buildDatasetToastMessage(datasetId) {
  const datasetUid = encodeID(datasetId, 'ds');
  return `Importing Darknet Dataset to ${datasetUid}`;
}

function navigateToDatasetCard(projectId, datasetId) {
  if (!projectId || !datasetId) {
    return;
  }

  const route = getDatasetRoute(projectId, datasetId);
  router.push(route);
}

async function createDatasetForImport() {
  const newDataset = getNewDatasetInfo();
  const response = await dataConnect.createDataset({
    name: newDataset.name,
    description: newDataset.description,
    annotation_set_name: newDataset.annotation_set_name || 'annotations',
    project_id: newDataset.project_id,
  });

  if (response?.error || !response?.result) {
    throw new Error(response?.error?.message || response?.error || 'Failed to create dataset');
  }

  createdDataset.value = response.result;

  return response.result;
}

async function s3MultipleFileUpload({ fileList, importParams, ignoreDuplicate = false }) {
  const keys = [];
  for (const file of fileList) {
    const relativePath = file.relativePath || file.webkitRelativePath;
    const trimmedPath = relativePath ? relativePath.split('/').slice(1) : [file.name];
    keys.push(trimmedPath.join('/'));
  }

  const presignedResponse = await dataConnect.s3CreatePresignedUrl({
    dataset_id: importParams.dataset_id,
    ignore_duplicate: ignoreDuplicate,
    keys,
  });

  if (presignedResponse?.error !== undefined) {
    throw presignedResponse.error;
  }

  const presignedResult = presignedResponse?.result || {};

  if (presignedResult.duplicate_count > 0 && !ignoreDuplicate) {
    throw new Error(`Destination dataset contains ${presignedResult.duplicate_count} duplicate file${presignedResult.duplicate_count === 1 ? '' : 's'}.`);
  }

  const newFileList = [];
  if (presignedResult) {
    const {
      importerSource, indexes = [], presignedUrls = [], tnPresignedUrls = [],
    } = presignedResult;

    if (importerSource) {
      importParams.importer_source = importerSource;
    }

    presignedUrls.forEach((url, index) => {
      newFileList.push({
        url,
        thumbnail_url: tnPresignedUrls[index],
        file: fileList[indexes[index]],
      });
    });
  }

  return importerStore.importByType({
    urls: newFileList,
    importParams,
  });
}

function validateRequiredFields() {
  const missingFields = [];
  if (!fileObjs.value.length) {
    missingFields.push(sourceUploadField.value?.label || 'Upload Dataset Files');
  }
  if (schemaGroupRef.value) {
    missingFields.push(...schemaGroupRef.value.validateRequiredFields());
  }
  return missingFields;
}

async function start() {
  const missingFields = validateRequiredFields();
  if (missingFields.length > 0) {
    const uniqueMissingFields = Array.from(new Set(missingFields));
    throw new Error(`Required field${uniqueMissingFields.length > 1 ? 's' : ''} missing: ${uniqueMissingFields.join(', ')}`);
  }

  if (isCreatingNewDataset()) {
    await createDatasetForImport();
  }

  const importParams = buildImportParams();
  if (!importParams.dataset_id) {
    throw new Error('A destination dataset is required.');
  }

  const importPromise = s3MultipleFileUpload({
    fileList: fileObjs.value,
    importParams,
  });

  $toast.open({
    queue: true,
    duration: 8000,
    dismissible: true,
    message: buildDatasetToastMessage(importParams.dataset_id),
    type: 'success',
    position: 'top',
  });
  emit('completed');
  navigateToDatasetCard(importParams.project_id, importParams.dataset_id);

  importPromise.catch((error) => {
    const errorMessage = error?.message || String(error || 'Darknet import failed');
    $toast.open({
      queue: true,
      duration: 0,
      message: errorMessage,
      type: 'error',
      dismissible: true,
      position: 'top',
    });
  });
}

defineExpose({
  start,
  validateRequiredFields,
});
</script>

<style lang="scss" scoped>
.darknet-importer {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.darknet-importer__heading {
  display: flex;
  align-items: center;
}

.darknet-importer__heading h4 {
  width: fit-content;
  margin-bottom: 0;
  font-size: 15px;
  white-space: nowrap;
  text-align: left;
  color: #250E81;
}

.field-group {
  margin-bottom: 16px;
}

.select,
.text-input {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: 10px 12px;
  background: #fff;
  box-sizing: border-box;
  min-height: 44px;
}

.text-input--textarea {
  resize: vertical;
}

.file-upload-btn {
  min-height: 180px;
  border: 2px dashed #b4b4b4;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.file-upload-btn._highlight {
  border-color: #250E81;
  background: rgba(37, 14, 129, 0.08);
}

.file-upload-btn__upload-icon {
  max-width: 70px;
  max-height: 70px;
}

.file-upload-btn__text {
  text-align: center;
  font-size: 14px;
  font-weight: 600;
}

.highlight {
  color: #250E81;
  cursor: pointer;
}

.highlight:hover {
  text-decoration: underline;
}

.file-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  max-height: 180px;
  width: 100%;
  margin-top: 12px;
}

.file-grid__cell {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
}

.file-grid__cell span {
  font-size: 13px;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.d-none {
  display: none;
}
</style>
